import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import Analytics from "./Analytics";
import AEQueries from "./AEQueries";
import ChatSessions from "./ChatSessions";
import Sandbox from "./Sandbox";
import AnswerEngineSettings from "./AnswerEngineSettings";
import KnowledgeBase from "./KnowledgeBase";
import { getLocalStorageWithExpiry } from "@/utils";
import "@/pages/page.css";

const VALID_ANSWER_ENGINE_TABS = new Set([
  "analytics",
  "ae-queries",
  "chat-sessions",
  "sandbox",
  "settings",
  "knowledge-base",
]);

export default function AnswerEngine() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab = VALID_ANSWER_ENGINE_TABS.has(requestedTab)
    ? requestedTab
    : "analytics";
  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);
  const answerEngineEnabled = getLocalStorageWithExpiry(
    "answer_engine_enabled",
  );

  const toggleSidebar = () => {
    setIsPermanentlyExpanded(!isPermanentlyExpanded);
  };

  return (
    <div className="app-container">
      <Sidebar
        currentPage="answer-engine"
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />

          {!answerEngineEnabled ? (
            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 mt-4">
              <i
                className="bi bi-lock text-muted mb-3"
                style={{ fontSize: "48px" }}
              ></i>
              <h5 className="fw-semibold text-dark">
                {t(
                  "answerEngine.notEnabledTitle",
                  "Answer Engine Analytics Not Enabled",
                )}
              </h5>
              <p className="text-muted mt-2" style={{ maxWidth: "420px" }}>
                {t(
                  "answerEngine.notEnabledMessage",
                  "This feature is not enabled for your current project. Please contact support to enable Answer Engine Analytics for this project.",
                )}
              </p>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                  <h3 className="h4 fw-bold text-dark mb-0">
                    {t("answerEngine.title", "Answer Engine")}
                  </h3>
                  <p className="mt-1 fs-6 text-secondary">
                    {activeTab === "analytics"
                      ? t(
                          "answerEngine.analyticsSubtitle",
                          "Monitor performance and user engagement metrics",
                        )
                      : activeTab === "ae-queries"
                        ? t(
                            "answerEngine.aeQueriesSubtitle",
                            "Browse and inspect queries processed by the Answer Engine",
                          )
                        : activeTab === "chat-sessions"
                          ? t(
                              "answerEngine.chatSessionsSubtitle",
                              "Browse individual chat sessions and their full conversations",
                            )
                          : activeTab === "sandbox"
                            ? t(
                                "answerEngine.sandboxSubtitle",
                                "Live preview with feedback & training tools",
                              )
                            : activeTab === "knowledge-base"
                              ? t(
                                  "answerEngine.knowledgeBaseSubtitle",
                                  "Manage your knowledge base URLs and pending ingestion requests",
                                )
                              : t(
                                  "answerEngine.settingsSubtitle",
                                  "Configure your Answer Engine settings",
                                )}
                  </p>
                </div>
              </div>

              <div className="answer-engine-content">
                {activeTab === "analytics" ? (
                  <Analytics />
                ) : activeTab === "ae-queries" ? (
                  <AEQueries />
                ) : activeTab === "chat-sessions" ? (
                  <ChatSessions />
                ) : activeTab === "sandbox" ? (
                  <Sandbox />
                ) : activeTab === "knowledge-base" ? (
                  <KnowledgeBase />
                ) : (
                  <AnswerEngineSettings />
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
